import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { MultiSelect } from '@/components/ui/multi-select';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  X, 
  Calendar, 
  User, 
  Building2, 
  Settings, 
  Clock, 
  FileText,
  CheckSquare,
  Edit3,
  Printer,
  Save,
  Tag,
  Plus,
  Trash2,
  Upload,
  Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function WorkOrderDetail({ workOrder, onClose, onUpdate, user, readOnly = false }) {
  const { t } = useLanguage();
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: workOrder.title,
    priority: workOrder.priority,
    status: workOrder.status,
    assignees: workOrder.assignees || [],  // Multi-select assignees
    department_id: workOrder.department_id || '',
    department_name: workOrder.department_name || '',
    machine_id: workOrder.machine_id || '',
    machine_name: workOrder.machine_name || '',
    due_date: workOrder.due_date ? workOrder.due_date.slice(0, 16) : '',
    scheduled_start: workOrder.scheduled_start ? workOrder.scheduled_start.slice(0, 16) : '',
    scheduled_end: workOrder.scheduled_end ? workOrder.scheduled_end.slice(0, 16) : '',
    estimated_duration: workOrder.estimated_duration || '',
    description: workOrder.description || '',
    safety_notes: workOrder.safety_notes || ''
  });
  const [checklist, setChecklist] = useState(workOrder.checklist || []);
  const [files, setFiles] = useState(workOrder.files || []);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [machines, setMachines] = useState([]);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [isChecklistSaving, setIsChecklistSaving] = useState(false);
  const abortRef = useRef(null);
  
  // Generate unique cache key for this work order
  const cacheKey = `equiptrack:checklist:kanban:${workOrder.id}`;
  
  // Load checklist from localStorage on mount (check for unsaved changes)
  useEffect(() => {
    const savedDraft = localStorage.getItem(cacheKey);
    if (savedDraft) {
      try {
        const draftChecklist = JSON.parse(savedDraft);
        // Only use draft if it's newer than current checklist
        if (draftChecklist.length !== checklist.length || 
            JSON.stringify(draftChecklist) !== JSON.stringify(checklist)) {
          setChecklist(draftChecklist);
        }
      } catch (error) {
        console.error('Failed to parse checklist draft:', error);
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      // Cancel any in-flight save on unmount
      abortRef.current?.abort();
    };
  }, []);
  
  // Sync files state when workOrder prop changes
  useEffect(() => {
    console.log('WorkOrder prop changed, files:', workOrder.files);
    if (workOrder.files && Array.isArray(workOrder.files)) {
      console.log('Setting files from workOrder:', workOrder.files);
      setFiles(workOrder.files);
    }
  }, [workOrder.files]);

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, []);
  
  // Fetch machines when department changes in edit mode
  useEffect(() => {
    if (editMode && formData.department_id) {
      fetchMachines(formData.department_id);
    }
  }, [editMode, formData.department_id]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/assignees`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching assignees:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };
  
  const fetchMachines = async (departmentId) => {
    try {
      const response = await axios.get(`${API}/machines?department_id=${departmentId}`);
      setMachines(response.data);
    } catch (error) {
      console.error('Error fetching machines:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this work order? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      
      await axios.delete(`${API}/work-orders/${workOrder.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success('Work order deleted successfully');
      
      if (onUpdate) {
        onUpdate(null); // Signal that the work order was deleted
      }
      onClose();
    } catch (error) {
      console.error('Failed to delete work order:', error);
      toast.error('Failed to delete work order');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Cancel any previous save request
      if (abortRef.current) {
        abortRef.current.abort();
      }
      
      // Create new AbortController for this request
      abortRef.current = new AbortController();

      // Only include fields that have actually changed or are essential
      const updateData = {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        priority: formData.priority,
        status: formData.status,
        assignees: formData.assignees.filter(id => id !== 'none'),  // Multiple assignees
        department_id: formData.department_id,
        department_name: formData.department_name,
        machine_id: formData.machine_id,
        machine_name: formData.machine_name,
        estimated_duration: formData.estimated_duration ? parseInt(formData.estimated_duration) : null,
        // Only include dates if they exist and are not empty strings
        ...(formData.due_date && { due_date: formData.due_date }),
        ...(formData.scheduled_start && { scheduled_start: formData.scheduled_start }),
        ...(formData.scheduled_end && { scheduled_end: formData.scheduled_end }),
        checklist: checklist,  // Include checklist in the save
        files: files  // Include files in the save
      };

      console.log('Sending to backend - scheduled_start:', formData.scheduled_start);
      console.log('Full update data:', updateData);

      const response = await axios.put(`${API}/work-orders/${workOrder.id}`, updateData, {
        signal: abortRef.current.signal
      });
      
      console.log('Received from backend - scheduled_start:', response.data.scheduled_start);
      console.log('Full response data:', response.data);
      
      // Use authoritative data from backend response
      const savedData = response.data;
      if (savedData.checklist) {
        setChecklist(savedData.checklist);
      }
      
      // Update files if present in response
      if (savedData.files) {
        setFiles(savedData.files);
      }
      
      // Clear localStorage draft after successful save
      localStorage.removeItem(cacheKey);
      
      // Trigger cross-page synchronization with full updated work order
      const updateEvent = new CustomEvent('workOrderUpdated', {
        detail: savedData  // Send the complete updated work order
      });
      window.dispatchEvent(updateEvent);
      
      toast.success('Work order and checklist updated successfully!');
      setEditMode(false);
      
      // Call onUpdate with full work order data so parent components can refresh
      if (onUpdate) {
        onUpdate(savedData);
      }
      // Modal should only close when user clicks X button
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Save request was aborted');
        return;
      }
      
      const message = error.response?.data?.detail || 'Failed to update work order';
      toast.error(message);
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const handleChecklistToggle = (itemId, completed) => {
    // Update local state only (no auto-save, no parent update)
    const updatedChecklist = checklist.map(item => 
      item.id === itemId 
        ? { 
            ...item, 
            completed, 
            completed_by: completed ? user.username : null,
            completed_at: completed ? new Date().toISOString() : null
          } 
        : item
    );
    
    setChecklist(updatedChecklist);
    // Save draft to localStorage for recovery
    localStorage.setItem(cacheKey, JSON.stringify(updatedChecklist));
  };

  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    
    const newItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // More unique ID
      text: newChecklistItem.trim(),
      completed: false,
      created_at: new Date().toISOString(),
      created_by: user.username
    };
    
    const updatedChecklist = [...checklist, newItem];
    setChecklist(updatedChecklist);
    setNewChecklistItem('');
    // Save draft to localStorage
    localStorage.setItem(cacheKey, JSON.stringify(updatedChecklist));
    toast.success('Checklist item added');
  };

  const removeChecklistItem = (itemId) => {
    const updatedChecklist = checklist.filter(item => item.id !== itemId);
    setChecklist(updatedChecklist);
    // Save draft to localStorage
    localStorage.setItem(cacheKey, JSON.stringify(updatedChecklist));
    toast.success('Checklist item removed');
  };

  const saveChecklistChanges = async () => {
    // Prevent multiple simultaneous saves
    if (isChecklistSaving) return;
    
    setIsChecklistSaving(true);
    
    try {
      // Cancel any previous save request
      if (abortRef.current) {
        abortRef.current.abort();
      }
      
      // Create new AbortController for this request
      abortRef.current = new AbortController();
      
      // Save to backend with abort signal
      const response = await axios.put(`${API}/work-orders/${workOrder.id}`, {
        checklist: checklist
      }, {
        signal: abortRef.current.signal
      });
      
      // Use authoritative data from backend response
      const savedChecklist = response.data.checklist || checklist;
      setChecklist(savedChecklist);
      
      // Clear localStorage draft after successful save
      localStorage.removeItem(cacheKey);
      
      // Trigger cross-page synchronization with authoritative data
      const updateEvent = new CustomEvent('workOrderUpdated', {
        detail: { workOrderId: workOrder.id, checklist: savedChecklist }
      });
      window.dispatchEvent(updateEvent);
      
      toast.success('Checklist saved successfully!');
      
      // Don't call onUpdate to prevent modal from closing
      // Parent component will refresh data when modal is actually closed
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Save request was aborted');
        return; // Don't show error for aborted requests
      }
      
      console.error('Failed to save checklist:', error);
      toast.error('Failed to save checklist');
    } finally {
      setIsChecklistSaving(false);
      abortRef.current = null;
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Upload file to server
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadResponse = await axios.post(
        `${API}/files/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const fileMetadata = uploadResponse.data;
      const updatedFiles = [...files, fileMetadata];
      setFiles(updatedFiles);
      
      // Save file metadata to work order
      const response = await axios.put(`${API}/work-orders/${workOrder.id}`, {
        files: updatedFiles
      });
      
      console.log('File upload response:', response.data);
      
      // Update files from backend response
      if (response.data.files) {
        setFiles(response.data.files);
      }
      
      // Update parent component with new data
      if (onUpdate) {
        onUpdate(response.data);
      }
      
      // Dispatch event for cross-page sync
      const updateEvent = new CustomEvent('workOrderUpdated', {
        detail: response.data
      });
      window.dispatchEvent(updateEvent);
      
      toast.success(`File "${file.name}" uploaded and saved successfully`);
    } catch (error) {
      console.error('Error saving file:', error);
      toast.error('File uploaded but failed to save');
    }
  };

  const removeFile = async (fileId) => {
    const updatedFiles = files.filter(file => file.id !== fileId);
    setFiles(updatedFiles);
    
    // Auto-save files immediately
    try {
      const response = await axios.put(`${API}/work-orders/${workOrder.id}`, {
        files: updatedFiles
      });
      
      console.log('File removal response:', response.data);
      
      // Update files from backend response
      if (response.data.files) {
        setFiles(response.data.files);
      }
      
      // Update parent component with new data
      if (onUpdate) {
        onUpdate(response.data);
      }
      
      // Dispatch event for cross-page sync
      const updateEvent = new CustomEvent('workOrderUpdated', {
        detail: response.data
      });
      window.dispatchEvent(updateEvent);
      
      toast.success('File removed and saved successfully');
    } catch (error) {
      console.error('Error saving after file removal:', error);
      toast.error('File removed but failed to save');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = generateWorkOrderPrintContent();
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const generateWorkOrderPrintContent = () => {
    const completedItems = workOrder.checklist?.filter(item => item.completed).length || 0;
    const totalItems = workOrder.checklist?.length || 0;
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.4; 
            }
            .header { 
              border-bottom: 2px solid #333; 
              padding-bottom: 10px; 
              margin-bottom: 20px; 
            }
            .title { 
              font-size: 24px; 
              font-weight: bold; 
              margin-bottom: 5px; 
            }
            .subtitle { 
              color: #666; 
              font-size: 14px; 
            }
            .section { 
              margin-bottom: 20px; 
            }
            .section-title { 
              font-size: 16px; 
              font-weight: bold; 
              margin-bottom: 10px; 
              border-bottom: 1px solid #ddd; 
              padding-bottom: 5px; 
            }
            .info-grid { 
              display: grid; 
              grid-template-columns: 1fr 1fr; 
              gap: 10px; 
              margin-bottom: 15px; 
            }
            .info-item { 
              display: flex; 
            }
            .info-label { 
              font-weight: bold; 
              min-width: 120px; 
            }
            .checklist-item { 
              margin: 8px 0; 
              display: flex; 
              align-items: center; 
            }
            .checkbox { 
              width: 16px; 
              height: 16px; 
              border: 1px solid #333; 
              margin-right: 10px; 
              display: inline-block; 
            }
            .checkbox.checked::after { 
              content: '✓'; 
              font-weight: bold; 
              display: block; 
              text-align: center; 
              line-height: 14px; 
            }
            .signature-section { 
              margin-top: 40px; 
              display: grid; 
              grid-template-columns: 1fr 1fr; 
              gap: 40px; 
            }
            .signature-box { 
              border-top: 1px solid #333; 
              padding-top: 5px; 
              text-align: center; 
            }
            @media print { 
              body { margin: 0; } 
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">Repair Work Order</div>
          </div>

          <div class="section">
            <div class="section-title">Task Information</div>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Task:</span>
                <span>${workOrder.title}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Department:</span>
                <span>${workOrder.department_name || 'N/A'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Date:</span>
                <span>${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Machine:</span>
                <span>${workOrder.machine_name || 'N/A'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Assignees:</span>
                <span>${workOrder.assignee_names && workOrder.assignee_names.length > 0 ? workOrder.assignee_names.join(', ') : (workOrder.assignee_name || 'Not assigned')}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Priority:</span>
                <span>${workOrder.priority}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Status:</span>
                <span>${workOrder.status}</span>
              </div>
            </div>
          </div>

          ${workOrder.description ? `
            <div class="section">
              <div class="section-title">Repair Instructions</div>
              <p>${workOrder.description}</p>
            </div>
          ` : ''}

          ${workOrder.safety_notes ? `
            <div class="section">
              <div class="section-title">Safety Notes</div>
              <p style="background: #fff3cd; padding: 10px; border: 1px solid #ffeaa7; border-radius: 4px;">
                ${workOrder.safety_notes}
              </p>
            </div>
          ` : ''}

          <div class="section">
            <div class="section-title" style="color: #dc3545;">Safety Reminder</div>
            <p style="background: #f8d7da; padding: 10px; border: 1px solid #f5c6cb; border-radius: 4px; font-weight: bold; color: #721c24;">
              Lock Down Power Supply and Use Safety Gear
            </p>
          </div>

          <div class="section">
            <div class="section-title">Checklist (${completedItems}/${totalItems} completed)</div>
            ${workOrder.checklist && workOrder.checklist.length > 0 ? 
              workOrder.checklist.map(item => `
                <div class="checklist-item">
                  <span class="checkbox ${item.completed ? 'checked' : ''}"></span>
                  <span>${item.text}</span>
                </div>
              `).join('') : 
              '<p>No checklist items defined.</p>'
            }
          </div>

          ${workOrder.tags && workOrder.tags.length > 0 ? `
            <div class="section">
              <div class="section-title">Tags</div>
              <p>${workOrder.tags.join(', ')}</p>
            </div>
          ` : ''}

          <div class="section">
            <div class="section-title">Notes/Comments</div>
            <div style="border: 1px solid #ddd; min-height: 80px; padding: 10px;">
              
            </div>
          </div>

          <div class="signature-section">
            <div class="signature-box">
              <div>Technician Signature</div>
            </div>
            <div class="signature-box">
              <div>Supervisor Signature</div>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Critical': 'bg-red-500 text-white',
      'High': 'bg-orange-500 text-white',
      'Medium': 'bg-yellow-500 text-white',
      'Low': 'bg-green-500 text-white'
    };
    return colors[priority] || 'bg-gray-500 text-white';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Backlog': 'bg-gray-100 text-gray-800',
      'Scheduled': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'On Hold': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const completedItems = checklist.filter(item => item.completed).length;
  const totalItems = checklist.length;
  const progressPercentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden bg-white">
        <CardHeader className="pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className="font-mono text-xs">
                    {workOrder.wo_id}
                  </Badge>
                  <Badge className={getPriorityColor(workOrder.priority)}>
                    {workOrder.priority}
                  </Badge>
                  <Badge className={getStatusColor(workOrder.status)}>
                    {workOrder.status}
                  </Badge>
                </div>
              </div>
              
              {editMode ? (
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="text-xl font-semibold mb-2"
                  data-testid="edit-wo-title"
                />
              ) : (
                <CardTitle className="text-xl text-gray-900">{workOrder.title}</CardTitle>
              )}
              
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span className="flex items-center">
                  <User className="w-4 h-4 mr-1" />
                  Requested by {workOrder.requested_by_name}
                </span>
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {formatDate(workOrder.created_at)}
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {!readOnly && !editMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditMode(true)}
                  data-testid="edit-work-order-btn"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  {t('edit')}
                </Button>
              )}
              
              {!readOnly && editMode && (
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="save-work-order-btn"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{t('saving')}</span>
                    </div>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t('save')}
                    </>
                  )}
                </Button>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                data-testid="print-work-order-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                {t('print')}
              </Button>
              
              {!readOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  disabled={loading || isChecklistSaving}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="delete-work-order-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('delete')}
                </Button>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // Block closing during save
                  if (isChecklistSaving) {
                    toast.info('Please wait for checklist to save before closing');
                    return;
                  }
                  
                  // Check for unsaved changes
                  const currentDraft = localStorage.getItem(cacheKey);
                  if (currentDraft && JSON.stringify(checklist) !== JSON.stringify(workOrder.checklist)) {
                    const proceed = window.confirm('You have unsaved checklist changes. Close anyway?');
                    if (!proceed) return;
                    
                    // Clear draft if user chooses to discard changes
                    localStorage.removeItem(cacheKey);
                  }
                  
                  if (onUpdate) {
                    // Refresh parent data when closing
                    onUpdate(workOrder);
                  }
                  onClose();
                }}
                className="h-8 w-8 p-0"
                data-testid="close-work-order-detail"
                disabled={isChecklistSaving}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <CardContent className="p-6">
            <Tabs defaultValue="details" className="space-y-6">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details">{t('details')}</TabsTrigger>
                <TabsTrigger value="checklist">
                  {t('checklist')} ({completedItems}/{totalItems})
                </TabsTrigger>
                <TabsTrigger value="files">{t('files')}</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">{t('priority')}</Label>
                      {editMode ? (
                        <Select 
                          value={formData.priority} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Low">{t('low')}</SelectItem>
                            <SelectItem value="Medium">{t('medium')}</SelectItem>
                            <SelectItem value="High">{t('high')}</SelectItem>
                            <SelectItem value="Critical">{t('critical')}</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="mt-1">
                          <Badge className={getPriorityColor(workOrder.priority)}>
                            {workOrder.priority}
                          </Badge>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">{t('status')}</Label>
                      {editMode ? (
                        <Select 
                          value={formData.status} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Scheduled">{t('scheduled')}</SelectItem>
                            <SelectItem value="In Progress">{t('inProgress')}</SelectItem>
                            <SelectItem value="Completed">{t('completed')}</SelectItem>
                            <SelectItem value="On Hold">{t('onHold')}</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="mt-1">
                          <Badge className={getStatusColor(workOrder.status)}>
                            {workOrder.status}
                          </Badge>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">{t('assignee')} (Multiple)</Label>
                      {editMode ? (
                        <MultiSelect
                          options={users.map(u => ({ value: u.id, label: u.name || u.username }))}
                          value={formData.assignees}
                          onChange={(selectedIds) => setFormData(prev => ({ ...prev, assignees: selectedIds }))}
                          placeholder="Select assignees..."
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900">
                          {workOrder.assignee_names && workOrder.assignee_names.length > 0 
                            ? workOrder.assignee_names.join(', ') 
                            : (workOrder.assignee_name || 'Not assigned')}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">Location</Label>
                      {editMode ? (
                        <div className="mt-1 space-y-3">
                          <div>
                            <Label className="text-xs text-gray-600">{t('department')}</Label>
                            <Select 
                              value={formData.department_id || 'none'} 
                              onValueChange={(value) => {
                                const dept = departments.find(d => d.id === value);
                                setFormData(prev => ({ 
                                  ...prev, 
                                  department_id: value === 'none' ? '' : value,
                                  department_name: dept?.name || '',
                                  machine_id: '', // Reset machine when department changes
                                  machine_name: ''
                                }));
                              }}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder={t('selectDepartment')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No department</SelectItem>
                                {departments.map(dept => (
                                  <SelectItem key={dept.id} value={dept.id}>
                                    {dept.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label className="text-xs text-gray-600">{t('machineEquipment')}</Label>
                            <Select 
                              value={formData.machine_id || 'none'} 
                              onValueChange={(value) => {
                                const machine = machines.find(m => m.id === value);
                                setFormData(prev => ({ 
                                  ...prev, 
                                  machine_id: value === 'none' ? '' : value,
                                  machine_name: machine?.name || ''
                                }));
                              }}
                              disabled={!formData.department_id || machines.length === 0}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder={formData.department_id ? t('selectMachine') : t('selectDepartmentFirst')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No specific machine</SelectItem>
                                {machines.map(machine => (
                                  <SelectItem key={machine.id} value={machine.id}>
                                    {machine.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-1 space-y-1">
                          <p className="text-sm text-gray-900">{workOrder.site}</p>
                          {workOrder.department_name && (
                            <p className="text-sm text-gray-600 flex items-center">
                              <Building2 className="w-4 h-4 mr-1" />
                              {workOrder.department_name}
                            </p>
                          )}
                          {workOrder.machine_name && (
                            <p className="text-sm text-gray-600 flex items-center">
                              <Settings className="w-4 h-4 mr-1" />
                              {workOrder.machine_name}
                            </p>
                          )}
                          {workOrder.location && (
                            <p className="text-sm text-gray-600">{workOrder.location}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">{t('dueDate')}</Label>
                      {editMode ? (
                        <Input
                          type="datetime-local"
                          value={formData.due_date}
                          onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900">
                          {formatDate(workOrder.due_date)}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">Scheduled Start</Label>
                      {editMode ? (
                        <Input
                          type="datetime-local"
                          value={formData.scheduled_start}
                          onChange={(e) => setFormData(prev => ({ ...prev, scheduled_start: e.target.value }))}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900">
                          {formatDate(workOrder.scheduled_start)}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">Scheduled End</Label>
                      {editMode ? (
                        <Input
                          type="datetime-local"
                          value={formData.scheduled_end}
                          onChange={(e) => setFormData(prev => ({ ...prev, scheduled_end: e.target.value }))}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900">
                          {formatDate(workOrder.scheduled_end)}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">Estimated Duration</Label>
                      {editMode ? (
                        <div className="flex items-center space-x-2 mt-1">
                          <Input
                            type="number"
                            step="0.5"
                            placeholder="e.g., 2.5"
                            value={formData.estimated_duration ? (formData.estimated_duration / 60).toFixed(1) : ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, estimated_duration: e.target.value ? Math.round(parseFloat(e.target.value) * 60) : null }))}
                            className="flex-1"
                          />
                          <span className="text-sm text-gray-500">hours</span>
                        </div>
                      ) : (
                        <p className="mt-1 text-sm text-gray-900">
                          {workOrder.estimated_duration ? `${(workOrder.estimated_duration / 60).toFixed(1)} hours` : 'Not set'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <Label className="text-sm font-medium text-gray-700">Repair Instructions</Label>
                  {editMode ? (
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      className="mt-1"
                      placeholder={t('detailedRepairInstructions')}
                    />
                  ) : (
                    <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                      {workOrder.description || 'No repair instructions provided.'}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">{t('safetyNotes')}</Label>
                  {editMode ? (
                    <Textarea
                      value={formData.safety_notes || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, safety_notes: e.target.value }))}
                      rows={3}
                      className="mt-1 bg-yellow-50"
                      placeholder={t('importantSafetyInfo')}
                    />
                  ) : (
                    <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap bg-yellow-50 p-3 rounded border border-yellow-200">
                      {workOrder.safety_notes || 'No safety notes specified.'}
                    </p>
                  )}
                </div>

                {workOrder.tags && workOrder.tags.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Tags</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {workOrder.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          <Tag className="w-3 h-3 mr-1" />
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="checklist" className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">{t('checklist')}</h3>
                  <div className="text-sm text-gray-600">
                    {completedItems} {t('of')} {totalItems} {t('completedLower')} ({Math.round(progressPercentage)}%)
                  </div>
                </div>

                {totalItems > 0 && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                )}

                {/* Add new checklist item - only in edit mode */}
                {editMode && (
                  <div className="flex space-x-2 p-4 bg-blue-50 rounded-lg">
                    <Input
                      type="text"
                      placeholder={t('addChecklistItem')}
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addChecklistItem()}
                      className="flex-1"
                      data-testid="new-wo-checklist-item"
                    />
                    <Button
                      onClick={addChecklistItem}
                      disabled={!newChecklistItem.trim()}
                      size="sm"
                      data-testid="add-wo-checklist-item"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                <div className="space-y-3">
                  {checklist.map((item, index) => (
                    <div key={item.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      <Checkbox
                        id={`checklist-${item.id}`}
                        checked={item.completed}
                        onCheckedChange={(checked) => handleChecklistToggle(item.id, checked)}
                        className="mt-0.5"
                        disabled={!editMode}
                        data-testid={`checklist-item-${index}`}
                      />
                      <div className="flex-1">
                        <Label
                          htmlFor={`checklist-${item.id}`}
                          className={`text-sm cursor-pointer ${
                            item.completed ? 'line-through text-gray-500' : 'text-gray-900'
                          }`}
                        >
                          {item.text}
                        </Label>
                        {item.completed && item.completed_by && (
                          <p className="text-xs text-gray-500 mt-1">
                            Completed by {item.completed_by} on {formatDate(item.completed_at)}
                          </p>
                        )}
                      </div>
                      {editMode && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeChecklistItem(item.id)}
                          className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Standalone Save Checklist button removed - saving integrated with main Edit/Save button */}

                {totalItems === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <CheckSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>No checklist items for this work order. Add maintenance procedures above.</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="files" className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">{t('filesDocumentation')}</h3>
                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="repair-file-upload"
                    />
                    <Label htmlFor="repair-file-upload">
                      <Button variant="outline" size="sm" className="cursor-pointer" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          {t('uploadFile')}
                        </span>
                      </Button>
                    </Label>
                  </div>
                </div>

                <div className="space-y-3">
                  {files.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{file.name}</p>
                          <p className="text-sm text-gray-500">
                            {formatFileSize(file.size)} • Uploaded by {file.uploaded_by} on {formatDate(file.uploaded_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              const response = await axios.get(
                                `${API}/files/download/${file.id}?stored_filename=${file.stored_filename}`,
                                { responseType: 'blob' }
                              );
                              
                              // Create blob URL and trigger download
                              const blob = new Blob([response.data]);
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = file.name;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(url);
                            } catch (error) {
                              console.error('Download error:', error);
                              toast.error('Failed to download file');
                            }
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(file.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {files.length === 0 && (
                  <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="mb-2">{t('noFilesUploaded')}</p>
                    <p className="text-sm">{t('uploadFilesDescription')}</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </div>
      </Card>
    </div>
  );
}

export default WorkOrderDetail;